import { createElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import { getProductLifecycleFileUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { jwtInstance } from '@/utils/api/jwt/instance';
import {
  buildPlmItemId,
  type PlmType,
} from '@/utils/approval/productLifecycle';
import { CARD_CLASS, DOWNLOAD_BUTTON_CLASS } from './content';
import { PlmCardActions, ProductLifecycleRoot } from './styles';

export { PlmCardActions, ProductLifecycleRoot } from './styles';

export { buildPlmCardHtml, buildPlmContentHtml } from './content';

/** 레거시 문서 카드의 버튼 */
const LEGACY_DOWNLOAD_BUTTON_CLASS = 'btn_apv_detail';
const LEGACY_DELETE_BUTTON_CLASS = 'btn-detail-hide';
const LEGACY_REJECT_BUTTON_CLASS = 'btn-detail-reject';

export type PlmRejectTarget = { itemId: string; styleNo: string };

/** 부분 반려 버튼을 달 카드 수 — V2 카드 + 레거시 카드 */
export const countPlmCards = (content: string) =>
  (content.match(/class="plm-card"/g)?.length ?? 0) +
  (content.match(/btn-detail-reject/g)?.length ?? 0);

/** 레거시 파일 이름 — pdfUrl 의 마지막 경로 */
const toFileName = (pdfUrl: string) => {
  const path = pdfUrl.split('?')[0];
  return path.slice(path.lastIndexOf('/') + 1) || 'detail.pdf';
};

const fileNameFromHeader = (disposition?: string) => {
  if (!disposition) return '';
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // 아래 filename= 으로
    }
  }
  return disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? '';
};

const downloadErrorMessage = (error: unknown) => {
  switch (axios.isAxiosError(error) ? error.response?.status : undefined) {
    case 404:
      return '상세내역 파일이 없습니다.';
    case 408:
      return 'GERP 응답이 늦어 상세내역을 받지 못했습니다. 잠시 후 다시 시도해 주세요.';
    case 422:
      return '내 미상신 목록에 없는 품번이라 상세내역을 받을 수 없습니다.';
    default:
      return '상세내역을 다운로드할 수 없습니다.';
  }
};

const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // 바로 지우면 브라우저가 파일명을 정하기 전에 주소가 사라져 blob ID 로 저장됨
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
};

/** 저장 HTML 에서 읽은 주소라 웹 주소만 엶 */
const isWebUrl = (url: string) => {
  try {
    return ['http:', 'https:'].includes(new URL(url).protocol);
  } catch {
    return false;
  }
};

/**
 * 상세내역 다운로드 — NEW_PLM 은 pdfUrl(CloudFront 주소)을 새 창으로만.
 * CENTRIC 은 작성 중 카드만 서버로 파일 받기(포탈 API 가 미상신 건만 받음), 조회·편집 화면은 안내
 */
export const usePlmDownload = () => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  return useCallback(
    async (
      load: string,
      pdfUrl: string,
      itemKey: string,
      allowFileDownload: boolean,
    ) => {
      const showError = (message = '상세내역을 다운로드할 수 없습니다.') =>
        snackbar.showError({ message: t(message) });
      if (load === 'NEW_PLM') {
        if (isWebUrl(pdfUrl)) window.open(pdfUrl, '_blank', 'noopener');
        else showError();
        return;
      }
      if (!allowFileDownload || !itemKey) {
        showError();
        return;
      }
      try {
        const res = await jwtInstance.get<Blob>(
          getProductLifecycleFileUrl('CENTRIC', itemKey),
          // GERP 를 거쳐 받아 기본 시간 제한보다 길게
          { responseType: 'blob', timeout: 60000 },
        );
        saveBlob(
          res.data,
          fileNameFromHeader(res.headers['content-disposition']) ||
            toFileName(pdfUrl),
        );
      } catch (error) {
        showError(downloadErrorMessage(error));
      }
    },
    [snackbar, t],
  );
};

/**
 * 저장 카드 HTML 그대로(레거시 문서 포함) — 다운로드 버튼만 포탈 버튼으로.
 * onPartialReject 를 주면 카드마다 부분반려 버튼
 */
export const ProductLifecycleSnapshot = ({
  content,
  plmType = 'CENTRIC',
  allowFileDownload = false,
  onPartialReject,
}: {
  content: string;
  /** 작성 중 카드만 — CENTRIC 파일을 서버에서 받음 */
  allowFileDownload?: boolean;
  /** 레거시 카드의 부분 반려 itemId 판단용 */
  plmType?: PlmType;
  onPartialReject?: (target: PlmRejectTarget) => void;
}) => {
  const { t } = useTranslation();
  const download = usePlmDownload();

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      const { classList } = element;
      const rejectButton = (target: PlmRejectTarget) => (
        <PlmCardActions>
          <BoxButton
            type="button"
            variant="secondary"
            size="small"
            onClick={() => onPartialReject?.(target)}
          >
            {t('부분반려')}
          </BoxButton>
        </PlmCardActions>
      );
      if (
        onPartialReject &&
        tagName === 'div' &&
        classList.contains(CARD_CLASS)
      ) {
        const itemId = element.getAttribute('data-item-id') ?? '';
        if (!itemId) return undefined;
        return createElement(
          'div',
          props,
          rejectButton({
            itemId,
            styleNo: element.getAttribute('data-style-no') ?? '',
          }),
          ...children,
        );
      }
      // 레거시 카드 위 삭제·부분반려 버튼 줄
      if (tagName === 'table' && element.querySelector('th[name="thdel"]')) {
        const legacy = element.querySelector(`.${LEGACY_REJECT_BUTTON_CLASS}`);
        if (!onPartialReject || !legacy) return null;
        const data = (name: string) => legacy.getAttribute(`data-${name}`);
        const styleNo = data('styleno') ?? '';
        return (
          <div key={props.key as string}>
            {rejectButton({
              itemId: buildPlmItemId(plmType, {
                aprvId: data('style_id'),
                brand: data('brand'),
                yy: data('yy'),
                styleNo,
              }),
              styleNo,
            })}
          </div>
        );
      }
      if (
        classList.contains(LEGACY_DELETE_BUTTON_CLASS) ||
        classList.contains(LEGACY_REJECT_BUTTON_CLASS)
      )
        return null;
      if (
        classList.contains(DOWNLOAD_BUTTON_CLASS) ||
        classList.contains(LEGACY_DOWNLOAD_BUTTON_CLASS)
      ) {
        // 레거시는 data-load 가 없으면 CENTRIC
        const load = element.getAttribute('data-load') || 'CENTRIC';
        const pdfUrl = element.getAttribute('data-pdfurl') ?? '';
        const itemKey = element.getAttribute('data-item-key') ?? '';
        return (
          <BoxButton
            key={props.key as string}
            type="button"
            variant="secondary"
            size="small"
            onClick={() => download(load, pdfUrl, itemKey, allowFileDownload)}
          >
            {t('상세내역 다운로드')}
          </BoxButton>
        );
      }
      return undefined;
    },
    [allowFileDownload, download, onPartialReject, plmType, t],
  );

  return (
    <ProductLifecycleRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </ProductLifecycleRoot>
  );
};
